import React, { useRef, useEffect } from "react";

interface SearchFieldProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  showClearButton?: boolean;
  onClear?: () => void;
  enableKeyboardShortcut?: boolean;
  keyboardShortcut?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}

const SearchField: React.FC<SearchFieldProps> = ({
  id = "search",
  name = "search",
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder = "Search...",
  className = "",
  showClearButton = true,
  onClear,
  enableKeyboardShortcut = false,
  keyboardShortcut = "⌘K",
  autoFocus = false,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Helper function to focus the input
  const focusInput = () => {
    const input = containerRef.current?.querySelector('input');
    if (input) {
      input.focus();
    }
  };

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    if (!enableKeyboardShortcut) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        focusInput();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enableKeyboardShortcut]);

  // Auto focus on mount if requested
  useEffect(() => {
    if (autoFocus) {
      focusInput();
    }
  }, [autoFocus]);

  // Handle clear button click
  const handleClear = () => {
    if (onClear) {
      onClear();
    } else {
      // Create synthetic event to clear the field
      const syntheticEvent = {
        target: { name, value: "", id }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
    focusInput();
  };

  // Handle key events on the container level
  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle Escape key to clear search
    if (e.key === 'Escape') {
      handleClear();
    }
    
    // Call provided onKeyDown handler if it's for the input
    if (onKeyDown && e.target instanceof HTMLInputElement) {
      onKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
    }
  };

  return (
    <div 
      ref={containerRef} 
      className={`relative ${className}`}
      onKeyDown={handleContainerKeyDown}
    >
      {/* Search Icon */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none z-10">
        <svg
          className="fill-current"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.04199 9.37363C3.04199 5.87693 5.87735 3.04199 9.37533 3.04199C12.8733 3.04199 15.7087 5.87693 15.7087 9.37363C15.7087 12.8703 12.8733 15.7053 9.37533 15.7053C5.87735 15.7053 3.04199 12.8703 3.04199 9.37363ZM9.37533 1.54199C5.04926 1.54199 1.54199 5.04817 1.54199 9.37363C1.54199 13.6991 5.04926 17.2053 9.37533 17.2053C11.2676 17.2053 13.0032 16.5344 14.3572 15.4176L17.1773 18.238C17.4702 18.5309 17.945 18.5309 18.2379 18.238C18.5308 17.9451 18.5309 17.4703 18.238 17.1773L15.4182 14.3573C16.5367 13.0033 17.2087 11.2669 17.2087 9.37363C17.2087 5.04817 13.7014 1.54199 9.37533 1.54199Z"
            fill=""
          />
        </svg>
      </div>

      {/* Search Input - using direct input with TailAdmin styling */}
      <input
        id={id}
        name={name}
        type="text"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`search-field-input h-11 w-full rounded-lg border appearance-none py-2.5 text-base font-light shadow-sm placeholder:text-gray-400 placeholder:!font-light focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 pl-12 pr-12 ${
          disabled
            ? "text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
            : "bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800"
        }`}
      />

      {/* Right side content */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 z-10">
        {/* Clear button */}
        {showClearButton && value && (
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Clear search"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Keyboard shortcut hint */}
        {enableKeyboardShortcut && !value && (
          <div className="text-xs text-gray-400 font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded border">
            {keyboardShortcut}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchField; 